import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import RwButton from './RwButton';
import type { RwButtonTone } from './RwButton';

/**
 * RwAlert — the DS Alert (rw-ds.css `.rw-alert`, rw-ds-components § Alert).
 *
 * Status/feedback surface — never a modal, never a field's own error, never colour-only
 * (tone + icon + neutral text always). Three variants by reach: `inline` (page flow),
 * `toast` (floating, top-right stack), `banner` (full-width, page/app state). Anatomy:
 * Storybook → Components → Feedback → Alert.
 */
export type RwAlertTone = 'information' | 'success' | 'warning' | 'critical';
export type RwAlertVariant = 'inline' | 'toast' | 'banner';

const ICON_BY_TONE: Record<RwAlertTone, ReactNode> = {
  information: <InfoOutlinedIcon />,
  success: <CheckCircleOutlinedIcon />,
  warning: <WarningAmberOutlinedIcon />,
  critical: <ErrorOutlineIcon />
};

/** Action button status per tone (rw-ds-components §Alert: "critical → destructive"). */
const ACTION_TONE_BY_ALERT_TONE: Record<RwAlertTone, { tone?: RwButtonTone; destructive?: boolean }> = {
  information: { tone: 'information' },
  success: { tone: 'success' },
  warning: { tone: 'warning' },
  critical: { destructive: true }
};

/** critical/warning = role="alert" (announced at once); information/success = role="status" (MUI-BRIDGE §Alert). */
const ROLE_BY_TONE: Record<RwAlertTone, 'alert' | 'status'> = {
  information: 'status',
  success: 'status',
  warning: 'alert',
  critical: 'alert'
};

export interface RwAlertProps {
  tone?: RwAlertTone;
  variant?: RwAlertVariant;
  title: ReactNode;
  description?: ReactNode;
  /** Renders a ghost/sm button in the alert's matching tone (critical → destructive). */
  actionLabel?: ReactNode;
  onAction?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Accessible name for the action button, when `actionLabel` alone isn't descriptive enough. */
  actionAriaLabel?: string;
  actionTestId?: string;
  /**
   * Marks the action button `aria-busy` while the work it started is pending. Deliberately NOT
   * `RwButton`'s `loading`, which also sets `disabled` — a disabled control drops keyboard focus,
   * and the retry button is the focused element. The caller guards its own `onAction` instead.
   */
  actionBusy?: boolean;
  /** Renders the ✕. Toast is always dismissable; omit on inline/banner when the alert blocks progress. */
  onDismiss?: () => void;
  /** Overrides the tone icon — e.g. a preview/avatar for a single-item toast confirmation. */
  thumbnail?: ReactNode;
  /** Banner-only: sticky to the top with a bottom divider while content scrolls beneath. */
  pinned?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
}

const RwAlert = forwardRef<HTMLDivElement, RwAlertProps>(function RwAlert(
  {
    tone = 'information',
    variant = 'inline',
    title,
    description,
    actionLabel,
    onAction,
    actionAriaLabel,
    actionTestId,
    actionBusy = false,
    onDismiss,
    thumbnail,
    pinned = false,
    className,
    sx,
    ...rest
  },
  ref
) {
  const classes = [
    'rw-alert',
    `rw-alert--${tone}`,
    variant !== 'inline' ? `rw-alert--${variant}` : '',
    variant === 'banner' && pinned ? 'is-pinned' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  const actionToneProps = ACTION_TONE_BY_ALERT_TONE[tone];

  return (
    <Box ref={ref} className={classes} role={ROLE_BY_TONE[tone]} sx={sx} {...rest}>
      <span className="rw-alert__icon">{thumbnail ?? ICON_BY_TONE[tone]}</span>
      <div className="rw-alert__body">
        <p className="rw-alert__title">{title}</p>
        {description != null && <p className="rw-alert__desc">{description}</p>}
        {actionLabel != null && onAction && (
          <div className="rw-alert__action">
            <RwButton
              tier="ghost"
              size="sm"
              onClick={onAction}
              aria-label={actionAriaLabel}
              aria-busy={actionBusy || undefined}
              data-testid={actionTestId}
              {...actionToneProps}
            >
              {actionLabel}
            </RwButton>
          </div>
        )}
      </div>
      {onDismiss && (
        <button type="button" className="rw-alert__close" aria-label="Dismiss" onClick={onDismiss}>
          <CloseIcon />
        </button>
      )}
    </Box>
  );
});

export default RwAlert;
